import { useEffect, useState } from 'react'
import { CircleHelp } from 'lucide-react'
import { FormDrawer } from '../../components/Drawer'
import QuestionFields from './QuestionFields'
import { EMPTY_QUESTION, validateQuestion } from './questionForm'
import { questionsStrings as S } from './strings'

/**
 * إضافة سؤال — the question form as a sheet, for the topic wizard.
 *
 * The bank writes its questions on their own page (QuestionFormPage). The wizard cannot
 * leave for one: a question added there lives only in the wizard until the topic is
 * saved, and navigating away would drop the topic half-written. So it stays a sheet over
 * the wizard, with the same fields as the page.
 *
 * @param {{id: number|null}} [ownerTopic]  the topic this question belongs to, when the form
 *   is opened from inside one. Present means the topic is DECIDED: the picker is not offered
 *   and not required. Absent means it must be asked.
 */
export default function QuestionFormDrawer({
  open,
  question,
  categories,
  levels,
  ownerTopic,
  onClose,
  onSave,
}) {
  const [values, setValues] = useState(EMPTY_QUESTION)
  const [errors, setErrors] = useState({})

  /* A question written inside a topic is filed under that topic — both branches of
     `SaveCategoryHandler` set it from the topic being saved and never read this — so it is
     seeded rather than asked. A topic being created has no id yet, and that is fine: the
     question travels in the same request that creates it.

     TWO PRIMITIVES, NOT THE PROP ITSELF. Callers write `ownerTopic={{ id }}` inline, so the
     object is new on every render — depending on it below would reseed the form as fast as
     it could be typed into. Neither of these changes unless the topic does. */
  const owned = Boolean(ownerTopic)
  const topicId = owned ? (ownerTopic.id ?? '') : null

  useEffect(() => {
    if (!open) return
    const seed = question ? { ...EMPTY_QUESTION, ...question, answer: String(question.answer ?? '') } : EMPTY_QUESTION
    setValues(owned ? { ...seed, category: topicId } : seed)
    setErrors({})
  }, [open, question, owned, topicId])

  const set = (key) => (value) => setValues((previous) => ({ ...previous, [key]: value }))

  const submit = (event) => {
    event.preventDefault()
    const found = validateQuestion(values, { askTopic: !owned })
    setErrors(found)
    if (Object.keys(found).length > 0) return
    onSave?.({ ...values, answer: Number(values.answer) })
  }

  return (
    <FormDrawer
      open={open}
      size="xl"
      formId="question-form"
      icon={CircleHelp}
      title={S.actions.create}
      subtitle={S.form.optionsPanel}
      submitLabel={S.form.save}
      cancelLabel={S.close}
      onClose={onClose}
      onSubmit={submit}
    >
      <QuestionFields
        values={values}
        errors={errors}
        set={set}
        categories={categories}
        levels={levels}
        askTopic={!owned}
      />
    </FormDrawer>
  )
}
